import { useParams } from "react-router-dom";
import { createContext, PropsWithChildren, useContext, useMemo, useEffect, useCallback, useRef, useState } from "react";
import { message } from "antd";
import { SETTINGS } from "shared";
import { socket } from "./socket";

type PROP_SETTINGS_CONTEXT = {
  settings: SETTINGS | undefined;
  saveSettings: (settings: Partial<SETTINGS>) => Promise<Partial<SETTINGS>>;
  reboot: () => void;
  // undefined until the server says
  uiPasswordSet: boolean | undefined;
  // "" removes it
  setUiPassword: (password: string) => Promise<void>;
};

const context = createContext<PROP_SETTINGS_CONTEXT>({
  settings: undefined,
  saveSettings: (_settings: Partial<SETTINGS>) => Promise.reject(),
  reboot: () => {},
  uiPasswordSet: undefined,
  setUiPassword: (_password: string) => Promise.reject(),
});

export const PropSettingsProvider = ({ children }: PropsWithChildren) => {
  const { prop_id } = useParams();
  const channel = useMemo(() => socket.channel(`prop_settings:${prop_id}`), [prop_id]);
  const [settings, setSettings] = useState<SETTINGS>();
  const [uiPasswordSet, setUiPasswordSet] = useState<boolean>();
  const [messageApi, contextHolder] = message.useMessage();
  const wasOnline = useRef<boolean>();

  // only on coming back: opening the page on an online prop says nothing
  useEffect(() => {
    const online = settings?.online;
    if (wasOnline.current === false && online) {
      messageApi.success(`${settings?.name ?? "The unit"} is back online`);
    }
    wasOnline.current = online;
  }, [settings?.online, settings?.name, messageApi]);

  useEffect(() => {
    socket.connect();
    channel.on("update", (newSettings) => {
      setSettings((s) => ({ ...s, ...newSettings }));
    });
    channel.join()
      .receive("ok", (settings) => {
        setSettings(settings);
        // not in the settings: they never carry the password's hash
        channel.push("ui_password", {})
          .receive("ok", ({ ui_password_set }: { ui_password_set: boolean }) => setUiPasswordSet(ui_password_set));
      })
      .receive("error", (e) => console.log(JSON.stringify(e)));

    return () => {
      channel.leave();
      setSettings(undefined);
      setUiPasswordSet(undefined);
    };
  }, [channel]);

  const saveSettings = useCallback((settings: Partial<SETTINGS>) => {
    return new Promise<Partial<SETTINGS>>((resolve, reject) => {
      channel.push("update_settings", settings)
        .receive("ok", (newSettings: SETTINGS) => {
          setSettings(newSettings);
          resolve(newSettings);
        })
        .receive("error", (e) => {
          console.error(JSON.stringify(e));
          reject(e);
        })
        // no reply in 10s: fail rather than leave the form saving
        .receive("timeout", () => reject("timeout"));
    });
  }, [channel]);

  const reboot = useCallback(() => {
    channel.push("reboot", {})
      .receive("error", (e) => console.error(JSON.stringify(e)));
  }, [channel]);

  const setUiPassword = useCallback((password: string) => {
    return new Promise<void>((resolve, reject) => {
      channel.push("set_ui_password", { password })
        .receive("ok", ({ ui_password_set }: { ui_password_set: boolean }) => {
          setUiPasswordSet(ui_password_set);
          resolve();
        })
        .receive("error", reject)
        .receive("timeout", () => reject("timeout"));
    });
  }, [channel]);

  return <context.Provider value={{ settings, saveSettings, reboot, uiPasswordSet, setUiPassword }}>
    {contextHolder}
    {children}
  </context.Provider>;
};

export const usePropSettings = () => useContext(context);
